import React, { createContext, useContext, useEffect, ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';
import { AuthUser, LoginCredentials } from '../types';

interface AuthContextData {
  user: AuthUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUserProfile: (profileData: Partial<AuthUser>) => void;
  refreshUserData: () => Promise<void>;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const store = useAuthStore();

  // Flag para evitar múltiplos logouts simultâneos
  const isLoggingOut = React.useRef(false);

  // Listener para sessão expirada (401 após tentar refresh)
  useEffect(() => {
    const handleSessionExpired = (event: Event) => {
      // Evita múltiplos logouts simultâneos
      if (isLoggingOut.current) {
        return;
      }

      isLoggingOut.current = true;

      // Fazer logout
      useAuthStore
        .getState()
        .logout()
        .finally(() => {
          isLoggingOut.current = false;
        });

      // A navegação será feita pelo componente que detectar isAuthenticated = false
    };

    window.addEventListener('session-expired', handleSessionExpired);

    return () => {
      window.removeEventListener('session-expired', handleSessionExpired);
    };
  }, []);

  useEffect(() => {
    // Só verifica autenticação se:
    // 1. Houve uma sessão anterior (hadSession = true)
    // 2. E NÃO há dados do usuário em cache (user = null)
    // Se já tem os dados do usuário no Zustand, não precisa chamar a API
    //
    // NÃO use [store] nas deps: qualquer isLoading/checkAuth re-dispara o efeito e, com
    // hadSession && !user, vira loop síncrono de re-renders + checkAuth (perfil com persist; anônimo limpo escapa).
    if (store.hadSession && !store.user) {
      useAuthStore
        .getState()
        .checkAuth()
        .then(() => {
          const { isAuthenticated } = useAuthStore.getState();
          if (!isAuthenticated) {
            useAuthStore.getState().setHadSession(false);
          }
        })
        .catch((error) => {
          // Erro na verificação de autenticação - limpar estado
          console.debug('Ô Erro ao verificar autenticação:', error?.message);
          useAuthStore.getState().setHadSession(false);
          useAuthStore.getState().setUser(null);
        });
    }
  }, [store.hadSession, store.user]);

  const value: AuthContextData = {
    user: store.user,
    isLoading: store.isLoading,
    isAuthenticated: store.isAuthenticated,
    error: store.error,
    login: store.login,
    logout: store.logout,
    clearError: store.clearError,
    updateUserProfile: store.updateUserProfile,
    refreshUserData: store.refreshUserData,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
